/**
 * FlowList Component
 *
 * Main list view for flows with search, filter, and actions.
 * Displays all flows with statistics, filtering, and CRUD operations.
 *
 * @component
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Play,
  Pause,
  Copy,
  Edit2,
  Trash2,
  Monitor,
  Zap,
  Clock,
  Database,
  Radio,
  MoreVertical,
  TrendingUp,
  CheckCircle,
  XCircle,
  Activity,
  Filter,
  RefreshCw
} from 'lucide-react';
import {
  StandardButton,
  StandardCard,
  StandardDataTable,
  StandardSearchBar,
  StandardSelect,
  StandardLoadingState,
  StandardEmptyState,
  StandardErrorState,
  useToast
} from '../../standard';
import { flowAutomationStorage, FLOW_TYPE_DEFINITIONS } from '../../../services/flowAutomationStorage';
import * as flowAutomationStorageRouter from '../../../services/flowAutomationStorageRouter';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';
import type { Flow, FlowType, FlowStatus, FlowStats } from '../../../types/flowAutomation';

// ============================================
// INTERFACES
// ============================================

interface FlowListProps {
  onCreateFlow: () => void;
  onEditFlow: (flowId: string) => void;
  onViewFlow: (flowId: string) => void;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

const getFlowTypeIcon = (type: FlowType) => {
  switch (type) {
    case 'screen_flow':
      return <Monitor size={16} className="text-blue-500" />;
    case 'auto_launched':
      return <Zap size={16} className="text-yellow-500" />;
    case 'scheduled':
      return <Clock size={16} className="text-purple-500" />;
    case 'record_triggered':
      return <Database size={16} className="text-green-500" />;
    case 'platform_event':
      return <Radio size={16} className="text-orange-500" />;
    default:
      return <Activity size={16} className="text-gray-500" />;
  }
};

const getStatusBadge = (status: FlowStatus) => {
  const styles = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    inactive: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    invalid: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  };

  const labels = {
    draft: 'Draft',
    active: 'Active',
    inactive: 'Inactive',
    invalid: 'Invalid'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

// ============================================
// STATS CARDS COMPONENT
// ============================================

interface StatsCardsProps {
  stats: FlowStats;
}

const StatsCards: React.FC<StatsCardsProps> = ({ stats }) => {
  const successRate = stats.totalRuns > 0
    ? Math.round((stats.successfulRuns / stats.totalRuns) * 100)
    : 0;

  const cards = [
    {
      label: 'Total Flows',
      value: stats.totalFlows,
      icon: Activity,
      color: 'blue'
    },
    {
      label: 'Active Flows',
      value: stats.activeFlows,
      icon: CheckCircle,
      color: 'green'
    },
    {
      label: 'Total Runs (30d)',
      value: formatNumber(stats.runsLast7Days * 4), // Approximate 30 days
      icon: TrendingUp,
      color: 'purple'
    },
    {
      label: 'Success Rate',
      value: `${successRate}%`,
      icon: successRate >= 90 ? CheckCircle : XCircle,
      color: successRate >= 90 ? 'green' : successRate >= 70 ? 'yellow' : 'red'
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    yellow: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map((card, index) => (
        <StandardCard key={index} className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {card.value}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${colorClasses[card.color as keyof typeof colorClasses]}`}>
              <card.icon size={24} />
            </div>
          </div>
        </StandardCard>
      ))}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const FlowList: React.FC<FlowListProps> = ({
  onCreateFlow,
  onEditFlow,
  onViewFlow
}) => {
  const { addToast } = useToast();

  const [flows, setFlows] = useState<Flow[]>([]);
  const [stats, setStats] = useState<FlowStats | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  useEffect(() => {
    refreshData();
  }, []);

  const refreshData = async () => {
    setIsLoading(true);
    try {
      const [flowList, statsData] = await Promise.all([
        flowAutomationStorageRouter.getAllFlowsAsync(),
        flowAutomationStorageRouter.getStatsAsync()
      ]);
      setFlows(flowList);
      setStats(statsData);
      setError(null);
    } catch (err) {
      setError('Failed to load flows');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter flows
  const filteredFlows = useMemo(() => {
    return flows.filter(flow => {
      // Search filter
      const matchesSearch = searchQuery === '' ||
        flow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        flow.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        flow.apiName.toLowerCase().includes(searchQuery.toLowerCase());

      // Type filter
      const matchesType = typeFilter === 'all' || flow.type === typeFilter;

      // Status filter
      const matchesStatus = statusFilter === 'all' || flow.status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [flows, searchQuery, typeFilter, statusFilter]);

  const handleActivate = async (flow: Flow) => {
    try {
      await flowAutomationStorageRouter.activateFlowAsync(flow.id);
      await refreshData();
      addToast({ type: 'success', title: 'Flow Activated', message: `"${flow.name}" is now active` });
    } catch (err) {
      addToast({ type: 'error', title: 'Activation Failed', message: err instanceof Error ? err.message : 'Failed to activate flow' });
    }
    setActiveMenu(null);
  };

  const handleDeactivate = async (flow: Flow) => {
    await flowAutomationStorageRouter.deactivateFlowAsync(flow.id);
    await refreshData();
    addToast({ type: 'success', title: 'Flow Deactivated', message: `"${flow.name}" is now inactive` });
    setActiveMenu(null);
  };

  const handleClone = async (flow: Flow) => {
    const cloned = await flowAutomationStorageRouter.cloneFlowAsync({
      sourceFlowId: flow.id,
      newName: `${flow.name} (Copy)`
    });
    if (cloned) {
      await refreshData();
      addToast({ type: 'success', title: 'Flow Cloned', message: `Created "${cloned.name}"` });
      onEditFlow(cloned.id);
    }
    setActiveMenu(null);
  };

  const handleDelete = async (flow: Flow) => {
    if (!window.confirm(`Are you sure you want to delete "${flow.name}"? This action cannot be undone.`)) {
      setActiveMenu(null);
      return;
    }
    const ok = await flowAutomationStorageRouter.deleteFlowAsync(flow.id);
    if (ok) {
      await refreshData();
      addToast({ type: 'success', title: 'Flow Deleted', message: `"${flow.name}" has been deleted` });
    }
    setActiveMenu(null);
  };

  const handleRun = (flow: Flow) => {
    // For screen flows, navigate to run page
    // For auto-launched, execute immediately
    addToast({
      type: 'info',
      title: 'Run Flow',
      message: `Running "${flow.name}"... (Demo mode)`
    });
    setActiveMenu(null);
  };

  // Type filter options
  const typeOptions = [
    { value: 'all', label: 'All Types' },
    ...FLOW_TYPE_DEFINITIONS.map(def => ({
      value: def.type,
      label: def.label
    }))
  ];

  // Status filter options
  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'draft', label: 'Draft' },
    { value: 'invalid', label: 'Invalid' }
  ];

  // Table columns
  const columns = [
    {
      key: 'name',
      label: 'Flow Name',
      render: (_value: unknown, flow: Flow) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
            {getFlowTypeIcon(flow.type)}
          </div>
          <div>
            <button
              onClick={() => onViewFlow(flow.id)}
              className="font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 text-left"
            >
              {flow.name}
            </button>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
              {flow.description || 'No description'}
            </p>
          </div>
        </div>
      )
    },
    {
      key: 'type',
      label: 'Type',
      render: (_value: unknown, flow: Flow) => {
        const typeDef = FLOW_TYPE_DEFINITIONS.find(t => t.type === flow.type);
        return (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {typeDef?.label || flow.type}
          </span>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (_value: unknown, flow: Flow) => getStatusBadge(flow.status)
    },
    {
      key: 'object',
      label: 'Object',
      render: (_value: unknown, flow: Flow) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {flow.recordTriggerConfig?.objectType || '-'}
        </span>
      )
    },
    {
      key: 'lastRun',
      label: 'Last Run',
      render: (_value: unknown, flow: Flow) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {formatDate(flow.lastRunDate)}
        </span>
      )
    },
    {
      key: 'runCount',
      label: 'Runs',
      render: (_value: unknown, flow: Flow) => (
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {formatNumber(flow.runCount)}
        </span>
      )
    },
    {
      key: 'lastModified',
      label: 'Last Modified',
      render: (_value: unknown, flow: Flow) => (
        <div className="text-sm">
          <p className="text-gray-600 dark:text-gray-400">
            {formatDate(flow.lastModifiedDate)}
          </p>
          <p className="text-gray-500 dark:text-gray-500 text-xs">
            by {flow.lastModifiedByName}
          </p>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value: unknown, flow: Flow) => (
        <div className="relative">
          <StandardButton
            variant="ghost"
            size="sm"
            onClick={() => setActiveMenu(activeMenu === flow.id ? null : flow.id)}
          >
            <MoreVertical size={16} />
          </StandardButton>

          {activeMenu === flow.id && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setActiveMenu(null)}
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 py-1">
                <button
                  onClick={() => onViewFlow(flow.id)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <Activity size={14} />
                  View Details
                </button>
                <button
                  onClick={() => onEditFlow(flow.id)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <Edit2 size={14} />
                  Edit Flow
                </button>
                {(flow.type === 'screen_flow' || flow.type === 'auto_launched') && (
                  <button
                    onClick={() => handleRun(flow)}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Play size={14} />
                    Run Flow
                  </button>
                )}
                <button
                  onClick={() => handleClone(flow)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <Copy size={14} />
                  Clone
                </button>
                <hr className="my-1 border-gray-200 dark:border-gray-700" />
                {flow.status === 'active' ? (
                  <button
                    onClick={() => handleDeactivate(flow)}
                    className="w-full px-4 py-2 text-left text-sm text-yellow-600 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Pause size={14} />
                    Deactivate
                  </button>
                ) : flow.status !== 'invalid' && (
                  <button
                    onClick={() => handleActivate(flow)}
                    className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Play size={14} />
                    Activate
                  </button>
                )}
                <button
                  onClick={() => handleDelete(flow)}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      )
    }
  ];

  // Render loading state
  if (isLoading) {
    return <StandardLoadingState message="Loading flows..." />;
  }

  // Render error state
  if (error) {
    return (
      <StandardErrorState
        title="Error Loading Flows"
        message={error}
        onRetry={refreshData}
      />
    );
  }

  return (
    <div>
      {stats && <StatsCards stats={stats} />}

      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
        <p className="text-sm text-yellow-700 dark:text-yellow-300">
          Showing {flows.length} flow{flows.length === 1 ? '' : 's'} persisted via{' '}
          {USE_GOBODO_API ? 'Gobodo API' : 'flowAutomationStorage (localStorage)'}.
        </p>
      </div>

      {/* Filters */}
      <StandardCard className="p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search flows by name, description, or API name..."
            />
          </div>
          <div className="flex gap-3">
            <StandardSelect
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              options={typeOptions}
              className="w-40"
            />
            <StandardSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={statusOptions}
              className="w-36"
            />
            <StandardButton
              variant="secondary"
              onClick={refreshData}
              leftIcon={<RefreshCw size={16} />}
            >
              Refresh
            </StandardButton>
          </div>
        </div>
      </StandardCard>

      {/* Flows Table */}
      {filteredFlows.length === 0 ? (
        <StandardEmptyState
          title="No Flows Found"
          message={
            searchQuery || typeFilter !== 'all' || statusFilter !== 'all'
              ? "No flows match your search criteria. Try adjusting your filters."
              : "You haven't created any flows yet. Create your first flow to get started."
          }
          actionLabel={!searchQuery && typeFilter === 'all' && statusFilter === 'all' ? "Create Your First Flow" : undefined}
          onAction={!searchQuery && typeFilter === 'all' && statusFilter === 'all' ? onCreateFlow : undefined}
        />
      ) : (
        <StandardCard>
          <StandardDataTable
            data={filteredFlows}
            columns={columns}
          />
        </StandardCard>
      )}
    </div>
  );
};

export default FlowList;
